/**
 * Resolves a notification's primary action into something the platform can navigate to.
 *
 * A producer attaches actions as `{ label, toolName, deepUrl }`. `toolName` names an SMP tool,
 * either by its menu display name ("Planogram") or by its registered asset id
 * ("ihm-virtual-store-tool-react-website"); `deepUrl` is a tool-relative hash path. The platform
 * opens the tool with that hash the same way the legacy notification center did.
 *
 * The action is always surfaced when the producer sent one. It is only *enabled* when the tool is
 * known, `deepUrl` is a well-formed tool-relative hash path, and the notification belongs to the
 * store the user is operating in (or to no store at all); otherwise the button renders disabled,
 * matching the "view only" treatment of other stores.
 */

import { _navigateToTool } from '@components/SideBar/utils/SideBarUtil'
import { getToolNameToDetailMap } from '@services/ToolMenuConfigService'
import { ITool } from '@atredux/slices/toolSlice'
import { IActionCenterAction, IActionCenterNotification } from './types'

export interface IResolvedAction {
    label: string
    /** False when the action exists but cannot be routed from here (other store, unknown tool). */
    enabled: boolean
    /** No-op when not enabled. */
    navigate: () => void
}

/** True when the notification belongs to the current store, or to no store at all. */
export const isActionableFromStore = (notification: IActionCenterNotification, currentStoreId: string | undefined): boolean =>
    !notification.storeId || (!!currentStoreId && notification.storeId === currentStoreId)

/**
 * Finds the SMP tool an action routes to. Matches the menu key (display name) first, then the
 * stable asset id / tool id so producers are not tied to a localisable label.
 */
export const findActionTool = (action: IActionCenterAction): ITool | undefined => {
    if (!action.toolName || !action.deepUrl) return undefined
    const tools = getToolNameToDetailMap()
    const byDisplayName = tools.get(action.toolName)
    if (byDisplayName) return byDisplayName
    for (const tool of tools.values()) {
        if (tool.displayNameStringId === action.toolName || tool.toolName === action.toolName) return tool
    }
    return undefined
}

const warnedTools = new Set<string>()
const warnUnknownTool = (toolName: string): void => {
    if (warnedTools.has(toolName)) return
    warnedTools.add(toolName)
    console.warn(`Action Center: notification action routes to unknown tool "${toolName}"`)
}

/** Longer than any route a tool serves; anything past this is not a deep link. */
const MAX_DEEP_URL_LENGTH = 2048

/**
 * True when `deepUrl` is a tool-relative hash path: `/route...` (optionally prefixed with `#`),
 * with no scheme or authority, no `.`/`..` segment (encoded or not), no backslash, and no
 * whitespace or control characters. The platform only ever appends the value as the fragment of
 * the tool's own URL, so this is defence in depth: a producer-supplied value never becomes a route
 * the tool did not mean to serve. Anything else renders the action disabled, like an unknown tool.
 */
export const isToolRelativeHashPath = (deepUrl: string | undefined): deepUrl is string => {
    if (!deepUrl || deepUrl.length > MAX_DEEP_URL_LENGTH) return false
    const path = deepUrl.startsWith('#') ? deepUrl.slice(1) : deepUrl
    // Leading '/' rules out schemes ("https:", "javascript:"); a second '/' would be an authority.
    if (!path.startsWith('/') || path.startsWith('//')) return false
    if (/[\s\\\u0000-\u001f\u007f]/.test(path)) return false
    const route = path.split(/[?#]/)[0]
    let decoded: string
    try {
        decoded = decodeURIComponent(route)
    } catch {
        return false
    }
    return !decoded.split('/').some((segment) => segment === '.' || segment === '..')
}

// Keyed on the tool, not the notification, so the cache is bounded by the tool menu like warnedTools.
const warnedDeepUrls = new Set<string>()
const warnBadDeepUrl = (toolName: string): void => {
    if (warnedDeepUrls.has(toolName)) return
    warnedDeepUrls.add(toolName)
    // The value itself is not logged: it is producer input that failed validation.
    console.warn(`Action Center: a notification action has a malformed deepUrl for tool "${toolName}"`)
}

/**
 * Picks the notification's primary action: the first one that can be routed, else the first one
 * the producer sent (rendered disabled). Undefined only when there are no actions at all.
 */
export function resolvePrimaryAction (
    notification: IActionCenterNotification,
    currentStoreId: string | undefined,
): IResolvedAction | undefined {
    const actions = notification.actions.filter((a) => !!a.label)
    if (actions.length === 0) return undefined

    const storeOk = isActionableFromStore(notification, currentStoreId)
    for (const action of actions) {
        const tool = findActionTool(action)
        if (!tool) {
            if (action.toolName) warnUnknownTool(action.toolName)
            continue
        }
        const deepUrl = action.deepUrl
        if (!isToolRelativeHashPath(deepUrl)) {
            warnBadDeepUrl(tool.displayName)
            continue
        }
        return {
            label: action.label,
            enabled: storeOk,
            navigate: () => { if (storeOk) _navigateToTool({ ...tool, hashPath: deepUrl }) },
        }
    }
    return { label: actions[0].label, enabled: false, navigate: () => undefined }
}
